<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import {useAbsenceReasonStore} from "@/stores/AbsenceReason";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import StrBalanceAutocomplete from "@/views/Absence/Balance/Components/StrBalanceAutocomplete.vue";
import axios from "axios";

export default {
    components: {StrBalanceAutocomplete},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const readonly = computed(() => props.deleteAction || props.viewAction);
        const {loading, overlay, dialog, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();
        const absenceReasonStore = useAbsenceReasonStore();


        const absenceReason = ref({
            allowOldRequest: false,
            includedInPay: false,
            requiredPeriod: false,
        });


        function findById(id) {
            loading.value = true;
            axios.get(`/absenceReasons/${id}`).then((response) => {
                absenceReason.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/absenceReasons', absenceReason.value).then((response) => {
                    absenceReasonStore.addAbsenceReason(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/absenceReasons', absenceReason.value).then((response) => {
                    absenceReasonStore.updateAbsenceReason(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/absenceReasons/${absenceReason.value.id}`).then(() => {
                absenceReasonStore.deleteAbsenceReason(absenceReason.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        })

        return {
            mxRules,
            readonly,
            absenceReasonStore,
            loading,
            overlay,
            dialog,
            inputColor,
            backgroundColor,
            absenceReason,
            save,
            update,
            remove,
            getBack,
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="700"
        scrollable
    >
        <v-card flat>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <v-card-text>
                <w-overlay
                    :loading="loading"
                    :overlay="overlay"
                    :delete-action="deleteAction"
                    @cancel="overlay = false"
                    @remove="remove"
                ></w-overlay>
                <v-card-text class="pa-0">
                    <v-container fluid>
                        <v-form ref="form" lazy-validation>
                            <w-fieldset :title="$t('common.details')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="absenceReason.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('absence_reason.unite')" required/>
                                        <w-enum-select
                                            v-model="absenceReason.unite"
                                            :items="[
                                                {id: 'HOURS', name: $t('absence_reason.HOURS')},
                                                {id: 'DAYS', name: $t('absence_reason.DAYS')},
                                            ]"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('absence_reason.mode')" required/>
                                        <w-enum-select
                                            v-model="absenceReason.mode"
                                            :items="[
                                                {id: 'CALENDAR', name: $t('absence_reason.CALENDAR')},
                                                {id: 'OPEN', name: $t('absence_reason.OPEN')},
                                            ]"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.color')" required/>
                                        <w-color-picker
                                            v-model="absenceReason.color"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('absence_reason.control')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-checkbox
                                            v-model="absenceReason.allowOldRequest"
                                            :label="$t('absence_reason.allow_old_request')"
                                            class="mt-n1"
                                            hide-details
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('absence_reason.days_before')"/>
                                        <w-text-field
                                            v-model="absenceReason.daysBefore"
                                            :readonly="readonly"
                                            type="number"
                                            step="1"
                                            :rules="[mxRules.integer]"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('absence_reason.min_days')"/>
                                        <w-text-field
                                            v-model="absenceReason.minDays"
                                            :readonly="readonly"
                                            type="number"
                                            step="1"
                                            :rules="[mxRules.integer]"
                                        />
                                    </v-col>
                                    <v-col cols="4">
                                        <w-label :label="$t('absence_reason.max_days')"/>
                                        <w-text-field
                                            v-model="absenceReason.maxDays"
                                            :readonly="readonly"
                                            type="number"
                                            step="1"
                                            :rules="[mxRules.integer]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('module.balance')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <str-balance-autocomplete
                                            v-model="absenceReason.balanceId"
                                            :readonly="readonly"
                                       />
                                    </v-col>

                                    <v-col cols="12">
                                        <v-checkbox
                                            v-model="absenceReason.includedInPay"
                                            color="red"
                                            :readonly="readonly"
                                            :label="$t('common.import')"
                                            hide-details
                                        />
                                    </v-col>

                                </v-row>
                                <v-row dense v-if="absenceReason.includedInPay">
                                    <v-col cols="12">
                                        <w-label :label="$t('pay_slip.pay_section')" required/>
                                        <text-field-pay-section
                                            v-model="absenceReason.paySectionCode"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>